import { Piano } from '@tunasong/icons'
import type { Chord } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { TypographyProps, ListItemProps } from '@tunasong/ui-lib'
import { Dialog, Divider, Typography, MenuItem, ListItemIcon, ListItemText } from '@tunasong/ui-lib'
import type { MouseEvent } from 'react'
import { useState } from 'react'
import { SimpleChord } from '../chord/simple-chord.js'
import { ScaleSelect } from '../scale/scale-select.js'
import { KeyboardChord } from './keyboard-chord.js'
import styles from './keyboard-menu.module.css'

interface KeyboardMenuItemProps extends Omit<ListItemProps, 'button'> {
  onClick?: () => void
  onClose?: () => void
  variant?: TypographyProps['variant']
}

export const useKeyboardMenuItem = (props: KeyboardMenuItemProps = {}) => {
  const { onClick, onClose, variant = 'body1' } = props

  const [scale, setScale] = useState<Scale | null>({ root: 'C', type: 'major' })
  const [chords, setChords] = useState<Chord[]>([])

  const [show, setShow] = useState(false)

  const handleShow = (ev: MouseEvent) => {
    ev.preventDefault()
    setShow(true)
    if (onClick) {
      onClick()
    }
  }

  const handleHide = () => {
    setShow(false)
    if (onClose) {
      onClose()
    }
  }

  return {
    dialog: (
      <Dialog fullScreen open={show} onClose={handleHide} title="Chord Finder" fullWidth={true} maxWidth="md">
        <ScaleSelect value={scale} onChange={setScale} />
        <Divider className={styles.divider} />
        <KeyboardChord scale={scale} showLabelOnActiveOnly={true} largeKeyboard={true} onChordChange={setChords} />

        <Divider className={styles.divider}>
          <Typography variant="caption">Identified Chords</Typography>
        </Divider>
        <div className={styles.chordsContainer}>
          {chords.map((c, idx) => (
            <SimpleChord className={styles.chordName} key={idx} chord={c} />
          ))}
        </div>
      </Dialog>
    ),
    menuItem: (
      <MenuItem title="Tuner" onClick={handleShow}>
        <ListItemIcon className={styles.menuIcon}>
          <Piano />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Keyboard chords</Typography>
        </ListItemText>
      </MenuItem>
    ),
  }
}
